import React, { useCallback, useEffect, useState } from 'react';
import { TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { makeStyles } from 'tss-react/mui';
import { Link } from 'react-router-dom';

import { TabPanel } from '../../../components/other/Tab/TabPanel';
import { ButtonWithLoader } from '../../../components/ui/Buttons';
import { useFormikRegistration } from '../hooks/useFormikRegistration';
import { useShowErrors } from '../../../hooks/useShowErrors';
import { TAuthTabs } from '.';
import { useAxiosRegistrations } from '../hooks/useAxiosRegistration';
import TermDialog from '../../../components/other/Dialog/TermDialog';

const useStyles = makeStyles()(theme => {
  return {
    toggleButtonGroup: {
      gap: 5,
      marginBottom: 15,
      '&.MuiToggleButtonGroup-lastButton': {
        'border-top-right-radius': '20px !important',
        'border-top-left-radius': '20px !important',
        'border-bottom-right-radius': '20px !important',
        'border-bottom-left-radius': '20px !important',
      },
    },
    toggleButton: {
      padding: '8px 33px',
      border: 'none',
      borderRadius: 20,
      fontSize: 13,
      textTransform: 'none',
      color: theme.palette.secondary.contrastText,
      '&.MuiToggleButtonGroup-lastButton, &.MuiToggleButtonGroup-firstButton': {
        'border-top-right-radius': '20px !important',
        'border-top-left-radius': '20px !important',
        'border-bottom-right-radius': '20px !important',
        'border-bottom-left-radius': '20px !important',
      },
    },
    toggleButtonActive: {
      backgroundColor: '#a3a7b833',
    },
  };
});

interface IProps {
  changeAuthTab: (newTab: TAuthTabs, props?: any) => void;
  initTab: number;
  initEmail?: string;
  isDialog?: boolean;
}

export const Registration: React.FC<IProps> = ({
  changeAuthTab,
  initTab,
  initEmail,
  isDialog,
}) => {
  const intl = useIntl();
  const [tab, setTab] = useState(initTab);

  const [alignment, setAlignment] = React.useState<string>(
    localStorage.getItem('alignment') || 'ROLE_VENDOR'
  );
  const classes1 = useStyles();
  const { classes } = classes1;
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogContentType, setDialogContentType] = useState('');
  const { data, loading, err, makeRequestReg, requestedType } = useAxiosRegistrations();
  const formik = useFormikRegistration(tab, initEmail, makeRequestReg, alignment);

  useEffect(() => {
    if (isDialog) {
      setAlignment('ROLE_BUYER');
    } else {
      localStorage.setItem('alignment', alignment);
    }
  }, [alignment]);

  // handle success
  useEffect(() => {
    if (data) {
      changeAuthTab('EmailSent', { email: formik.values.email });
    }
  }, [data, requestedType, formik.values.email]);

  useShowErrors([err]);

  const handleAlignment = (event: React.MouseEvent<HTMLElement>, newAlignment: string) => {
    setAlignment(newAlignment);
  };

  const handleDialogOpen = useCallback(
    (contentType: string) => {
      setDialogContentType(contentType);
      setOpenDialog(true);
    },
    [setDialogContentType, setOpenDialog]
  );

  return (
    <>
      <div className='kt-login__body' style={{ position: 'relative' }}>
        <div className='kt-login__form'>
          <div className='kt-login__title'>
            <h3>
              {intl.formatMessage({
                id:
                  alignment === 'ROLE_VENDOR'
                    ? 'AUTH.REGISTER.NEW_USER.VENDOR'
                    : 'AUTH.REGISTER.NEW_USER.BUYER',
              })}
            </h3>
          </div>

          <form
            noValidate
            autoComplete='off'
            className='kt-form'
            onSubmit={formik.handleSubmit}
          >
            {!isDialog ? (
              <ToggleButtonGroup
                value={alignment}
                exclusive
                onChange={handleAlignment}
                aria-label='text alignment'
                className={classes.toggleButtonGroup}
              >
                <ToggleButton
                  className={`${classes.toggleButton} ${
                    alignment === 'ROLE_BUYER' ? classes.toggleButtonActive : ''
                  }`}
                  value='ROLE_BUYER'
                >
                  {intl.formatMessage({ id: 'USER.ROLES.BUYER' })}
                </ToggleButton>
                <ToggleButton
                  className={`${classes.toggleButton} ${
                    alignment === 'ROLE_VENDOR' ? classes.toggleButtonActive : ''
                  }`}
                  value='ROLE_VENDOR'
                >
                  {intl.formatMessage({ id: 'USER.ROLES.VENDOR' })}
                </ToggleButton>
              </ToggleButtonGroup>
            ) : null}
            {/* email */}
            <TabPanel value={tab} index={0} p={0}>
              <div className='form-group'>
                <TextField
                  type='text'
                  label={intl.formatMessage({ id: 'AUTH.INPUT.EMAIL' })}
                  margin='normal'
                  className='kt-width-full'
                  name='email'
                  variant='outlined'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.email}
                  helperText={formik.touched.email && formik.errors.email}
                  error={Boolean(formik.touched.email && formik.errors.email)}
                />
              </div>
              <div className='form-group'>
                <p>{intl.formatMessage({ id: 'AUTH.REGISTER.EMAIL.INFO' })}</p>
              </div>
            </TabPanel>

            <div className='kt-login__actions' style={{ margin: '32px 0' }}>
              <ButtonWithLoader
                style={{
                  width: '100%',
                  backgroundColor: !formik.values.email ? '#7c97c080' : '#205373',
                  color: '#fff',
                }}
                onPress={formik.handleSubmit}
                disabled={!formik.values.email}
                loading={loading}
              >
                {intl.formatMessage({ id: 'AUTH.GENERAL.SIGNUP_BUTTON' })}
              </ButtonWithLoader>
            </div>
            <div
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Link
                to='#'
                onClick={() => changeAuthTab('CheckInSystem')}
                className='kt-link kt-login__link-forgot'
                style={{ color: '#006997' }}
              >
                {intl.formatMessage({ id: 'AUTH.REGISTER.LOGIN_WITH_PASSWORD' })}
              </Link>
            </div>
          </form>
          <div className='kt-login__menu' style={{ textAlign: 'center', marginTop: 20 }}>
            <p style={{ color: '#0d0a05' }}>
              Нажимая на кнопку Зарегистрироваться, Вы соглашаетесь с{' '}
              <a
                className='kt-link'
                onClick={() => handleDialogOpen('privacy_policy')}
                style={{ marginRight: 0, whiteSpace: 'nowrap' }}
              >
                <FormattedMessage id='AUTH.LOGIN.PRIVACY_POLICY' />
              </a>{' '}
              и на обработку ваших{' '}
              <a
                className='kt-link'
                onClick={() => handleDialogOpen('user_agreement')}
                style={{ marginRight: 0, whiteSpace: 'nowrap' }}
              >
                <FormattedMessage id='AUTH.LOGIN.LEGAL_TERMS' />
              </a>
            </p>
          </div>
        </div>
      </div>
      <TermDialog
        isOpen={openDialog}
        handleClose={() => setOpenDialog(false)}
        contentType={dialogContentType}
      />
    </>
  );
};
